import { deiterateAPI } from "../utils/deiterateAPI.util";
import { window } from "../utils/window.util";

export namespace AssetRegister {

    export interface ChangeItem {
        elementName: string;
        newValue: any;
        oldValue: any;
    }

    export interface AssetHistoryItem {
        changedBy: string;
        changedDate: Date;
        changes: {
            [key: string]: ChangeItem;
        };
    }

    export interface AssetEntity {
        componentType: string;
        assetCat: string;
        assetCriticality: string;
        assetDesc: string;
        assetHistory: AssetHistoryItem[];
        assetID: string;
        assetIDstatus: "active" | "inactive" | "deleted";
        backupHistoryMap: { [key: string]: any };
        assetLocation: string;
        assetOwner: string;
        assetPublishInPrivacyPolicy: "Yes" | "No";
        usesAI: "Yes" | "No";
        assetRisk: string;
        assetSubprocessor: "Yes" | "No";
        assetTeam: string;
        backupLocation: string;
        backupNeeded: "Yes" | "No";
        backupRetention: string;
        classification: string;
        customFields: any[];
        dataStorageLocations: string[];
        dataTypesShared: string[];
        dataTypesSharedString: string;
        dateAdded: Date;
        label?: string;
        physicalLabel?: string;
        lastReviewDate: Date;
        legalBits: string;
        nextReviewDate: Date;
        portability: "Yes" | "No";
        securityReview: "Yes" | "No";
        serial: string;
        socCriticality: string;
        socSubserviceOrganisation: string;
        status: "Active" | "Decommissioned" | "Deleted";
        vendorManagementNeeded: "Yes" | "No";
        vendorWebsite: string;
        componentId?: string;
        relatedControls?: string[];
        securityScore?: number;
        lastUpguardCheck?: {
            [domain: string]: {
                lastChecked: string;
            };
        };
        upguardSecurityScoreHistory?: {
            [key: string]: UpguardScoreEntry | number;
        } & {
            idHistory: number;
        };
        risks?: string[];
    }

    export interface UpguardScoreEntry {
        score: string;
        autoCheck: boolean;
        checkDate: string;
    }

    export interface UpguardHistoryResponse {
        statusCode: number;
        message: string;
        upguardSecurityScoreHistory: {
            [key: string]: UpguardScoreEntry | number;
        } & {
            idHistory: number;
        };
    }

    export interface AssetRegisterResponse {
        statusCode?: number;
        message?: string;
        assets?: AssetEntity[];
        entry?: AssetEntity;
        error?: string | null;
    }

    export class AssetInstance {
        private data: AssetRegister.AssetEntity;

        constructor(data: AssetRegister.AssetEntity) {
            this.data = data;
        }

        // A getter to access the underlying data if needed
        public get entity(): AssetRegister.AssetEntity {
            return this.data;
        }

        // An edit method that calls the static Edit function on the Asset class
        public async edit(updates: Partial<AssetRegister.AssetEntity>): Promise<AssetRegister.AssetRegisterResponse | void> {
            return AssetRegister.Asset.Edit(this.data.assetID, updates);
        }
    }

    export class Asset {

        /**
         * Add an asset to the API.
         * @param id - The ID of the asset to add.
         * @param assetData - Partial asset data to add.
         * @param callback - Optional callback after response.
         */
        static async Add(
            id: string,
            assetData: Partial<AssetEntity>,
            callback?: (res: AssetRegisterResponse) => any
        ): Promise<AssetRegisterResponse | void> {
            console.log("Calling API Add", { companyID: window.companyID, ...assetData });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/assets/add/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID, ...assetData })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }


        /**
         * Delete an asset from the API.
         * @param id - The ID of the asset to delete.
         * @param callback - Optional callback after response.
         */
        static async Delete(
            id: string,
            callback?: (res: AssetRegisterResponse) => any
        ): Promise<AssetRegisterResponse | void> {
            console.log("Calling API Delete", { companyID: window.companyID });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/assets/delete/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }


        /**
         * Edit an existing asset in the API.
         * @param id - The ID of the asset to edit.
         * @param assetData - Partial asset data to update.
         * @param callback - Optional callback after response.
         */
        static async Edit(
            id: string,
            assetData: Partial<AssetEntity>,
            callback?: (res: AssetRegisterResponse) => any
        ): Promise<AssetRegisterResponse | void> {
            console.log("Calling API Edit", { companyID: window.companyID, ...assetData });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/assets/edit/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID, ...assetData })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }

        /**
         * Fetch all assets from the API.
         */
        static async Get(): Promise<AssetRegister.AssetEntity[] | undefined>;

        /**
         * Fetch a specific asset by ID from the API.
         * @param id - The ID of the asset to fetch.
         */
        static async Get(id: string | number): Promise<AssetInstance | undefined>;

        /**
         * Fetch a only a specific assets from the API.
         * @param leaveNull | NULL
         * @param type - Type of assets to fetch (asset, supplier)
         */
        static async Get(leaveNull: null, type: string): Promise<AssetInstance | undefined>;

        static async Get(id?: string | number | null, type?: string): Promise<AssetRegister.AssetEntity[] | AssetInstance | undefined> {
            if (id === null && type) {
                // Fetch all assets
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/assets/get/all`, {
                    mode: 'cors',
                    body: JSON.stringify({ companyID: window.companyID })
                });

                // Filter res.assets based on type
                console.log('Filtering assets by type: ', type);

                // If the type is not provided, return all assets
                if (!type) {
                    return res.assets;
                }

                const normalizedType = type.toLowerCase().endsWith('s')
                    ? type.toLowerCase().slice(0, -1)
                    : type.toLowerCase();

                // If the type is "asset", treat componentType === undefined as an asset
                if (normalizedType === 'asset') {
                    return res.assets.filter(
                        (asset: AssetEntity) =>
                            asset?.componentType === undefined ||
                            asset?.componentType.toLowerCase() === 'asset'
                    );
                }

                // Otherwise, filter by type
                return res.assets.filter(
                    (asset: AssetEntity) =>
                        asset?.componentType !== undefined &&
                        asset?.componentType.toLowerCase() === normalizedType
                );
            } else if (typeof id === 'undefined' || id === 'all') {
                // Fetch all assets
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/assets/get/all`, {
                    mode: 'cors',
                    body: JSON.stringify({ companyID: window.companyID })
                });
                return res.assets;
            } else {
                try {
                    // Fetch single asset first
                    const assetRes = await deiterateAPI.POST(`/deiterateInternalAPI/assets/get/${id}`, {
                        mode: 'cors',
                        body: JSON.stringify({ companyID: window.companyID })
                    });

                    if (!assetRes.entry) {
                        return undefined;
                    }

                    // If asset exists, fetch history data
                    try {
                        const [historyRes, upguardRes, backupHistoryRes] = await Promise.all([
                            deiterateAPI.POST(`/deiterateInternalAPI/assets/history/${id}`, {
                                mode: 'cors',
                                body: JSON.stringify({ companyID: window.companyID })
                            }),
                            deiterateAPI.POST(`/deiterateInternalAPI/assets/upguard/${id}`, {
                                mode: 'cors',
                                body: JSON.stringify({ companyID: window.companyID })
                            }),
                            deiterateAPI.POST(`/deiterateInternalAPI/assets/backup-history/${id}`, {
                                mode: 'cors',
                                body: JSON.stringify({ companyID: window.companyID })
                            })
                        ]);

                        // Combine the asset data with its history
                        const completeAsset = {
                            ...assetRes.entry,
                            assetHistory: historyRes?.assetHistory || [],
                            upguardSecurityScoreHistory: upguardRes?.upguardSecurityScoreHistory || [],
                            backupHistoryMap: backupHistoryRes?.backupHistoryMap || {}
                        };
                        return new AssetInstance(completeAsset);
                    } catch (historyError) {
                        console.error('Error fetching history data:', historyError);
                        // Return asset without history if history fetch fails
                        return new AssetInstance({
                            ...assetRes.entry,
                            assetHistory: [],
                            upguardSecurityScoreHistory: [],
                            backupHistoryMap: {}
                        });
                    }
                } catch (error) {
                    console.error('Error fetching asset:', error);
                    return undefined;
                }
            }
        }

        static async Menu(): Promise<any> {
            // Fetch all assets
            const res = await deiterateAPI.POST(`/deiterateInternalAPI/assets/get/menu`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID })
            });
            return res.menu;
        }

        /**
         * Fetch asset history for a specific asset.
         * @param id - The ID of the asset to fetch history for.
         */
        static async GetHistory(id: string): Promise<AssetHistoryItem[] | undefined> {
            try {
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/assets/history/${id}`, {
                    mode: 'cors',
                    body: JSON.stringify({ companyID: window.companyID })
                });
                return res.assetHistory || [];
            } catch (error) {
                console.error('Error fetching asset history:', error);
                return undefined;
            }
        }

        /**
         * Fetch Upguard security score history for a specific asset.
         * @param id - The ID of the asset to fetch Upguard history for.
         */
        static async GetUpguardHistory(id: string): Promise<UpguardHistoryResponse | undefined> {
            try {
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/assets/upguard/${id}`, {
                    mode: 'cors',
                    body: JSON.stringify({ companyID: window.companyID })
                });
                return res;
            } catch (error) {
                console.error('Error fetching Upguard history:', error);
                return undefined;
            }
        }

        /**
         * Get the latest Upguard security score for a specific asset.
         * @param id - The ID of the asset to fetch the latest score for.
         * @returns The latest score string (e.g., "713/950") or "0/950" if no score is available
         */
        static async GetLatestUpguardScore(id: string): Promise<string> {
            try {
                const history = await this.GetUpguardHistory(id);
                if (!history?.upguardSecurityScoreHistory) return "0/950";

                const entries = Object.entries(history.upguardSecurityScoreHistory)
                    .filter(([key]) => key !== 'idHistory')
                    .map(([_, value]) => value as UpguardScoreEntry)
                    .sort((a, b) => new Date(b.checkDate).getTime() - new Date(a.checkDate).getTime());

                return entries.length > 0 ? entries[0].score : "0/950";
            } catch (error) {
                console.error('Error getting latest Upguard score:', error);
                return "0/950";
            }
        }

        /**
         * Fetch backup history for a specific asset.
         * @param id - The ID of the asset to fetch backup history for.
         */
        static async GetBackupHistory(id: string): Promise<{ [key: string]: any } | undefined> {
            try {
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/assets/backup-history/${id}`, {
                    mode: 'cors',
                    body: JSON.stringify({ companyID: window.companyID })
                });
                return res.backupHistoryMap || {};
            } catch (error) {
                console.error('Error fetching backup history:', error);
                return undefined;
            }
        }
    }
}